import Storage, { cacheBucket, cacheSuffix, StorageOptions } from "PFCore/base/storage/storage";
import { endsWith, startsWith } from "underscore.string";

export default class BrowserStorage extends Storage {
  // fallow-ignore-next-line unused-class-member -- called polymorphically via window.storage: Storage
  setItem(key: string, value: any, options: StorageOptions = {}): void | boolean {
    this.flushExpired();

    const stringValue = this.stringify(value);
    try {
      window.localStorage.setItem(this.storageKey(key), stringValue as string);
    } catch (error) {
      if (this.isOutOfSpace(error as Error)) {
        console.warn(`Could not add item with key '${key}', perhaps it's too big?`, error);
      } else {
        console.warn(`Could not add item with key '${key}'`, error);
      }
      return false;
    }

    if (options.expiresIn) {
      window.localStorage.setItem(this.expirationKey(key), this.expirationTimeFor(options.expiresIn));
    } else {
      if (!options.preserveTime) {
        window.localStorage.removeItem(this.expirationKey(key));
      }
    }
  }

  // fallow-ignore-next-line unused-class-member -- called polymorphically via window.storage: Storage
  getItem(key: string): any {
    this.flushExpiredItem(key);

    const value = window.localStorage.getItem(this.storageKey(key));
    try {
      return JSON.parse(value as string);
    } catch {
      return value;
    }
  }

  removeItem(key: string): void {
    window.localStorage.removeItem(this.storageKey(key));
    window.localStorage.removeItem(this.expirationKey(key));
  }

  // fallow-ignore-next-line unused-class-member -- called polymorphically via window.storage: Storage
  clear(): void {
    window.localStorage.clear();
  }

  // fallow-ignore-next-line unused-class-member -- called polymorphically via window.storage: Storage
  key(index: number): string | null {
    return window.localStorage.key(index);
  }

  eachKey(callback: (key: string) => void): void {
    Object.keys(window.localStorage).forEach((key) => {
      if (key && startsWith(key, cacheBucket) && !endsWith(key, cacheSuffix)) {
        callback(key.replace(cacheBucket, ""));
      }
    });
  }

  expirationTime(key: string): number | null {
    const time = window.localStorage.getItem(this.expirationKey(key));
    if (time) {
      return parseInt(time, 10);
    } else {
      return null;
    }
  }
}
